/**
 * groupByStore — the by-store grouping shared by the Shopping list
 * (`ShoppingModeView`, in-store) and the Trip screen (`TripPage`, T27.2.7.9.3).
 *
 * Lifted out of `ShoppingModeView.tsx` (no behavior change) so the Trip view can
 * reuse the exact same grouping without coupling trip → the lists feature's
 * component module. `ShoppingModeView` re-exports `groupByStore` / `UNKNOWN_STORE_LABEL`
 * from here, so its existing tests keep importing them from `./ShoppingModeView`.
 *
 * Pure: no React, no platform globals. Groups items by the item's selected store
 * LOCATION (`selected_storelocation_id`), NOT by name — so two same-named
 * locations (two "Metro" branches) form two distinct groups (T41). The visible
 * label still reads the store name (`display_store` / `store_name`); the branch
 * label that disambiguates same-chain locations is resolved by the view from the
 * `useStores()` row keyed on `storeId`. Items with no assigned store collect into
 * one trailing group keyed `UNKNOWN_STORE` (`storeId: null`) — a store is NEVER
 * fabricated.
 */

import type { ShoppingListItemWithDetails } from '@shopista/shared/types';

// The flat group key used for items the household has not assigned to a store.
// Never invent a store name — these collect under one honest header that says
// plainly what they are: items with no store picked, grab them wherever you are.
export const UNKNOWN_STORE = '__unassigned__';
export const UNKNOWN_STORE_LABEL = 'no store picked — grab anywhere';

export interface StoreGroup {
  key: string;
  label: string;
  /**
   * T41 — the `selected_storelocation_id` this group keys on, or `null` for the
   * unassigned "grab anywhere" bucket. The view resolves the `StoreLocation` row
   * from this id (via `useStores()`) for the branch label and the switcher chip,
   * so two same-named locations stay distinct. The label still reads the name.
   */
  storeId: string | null;
  items: ShoppingListItemWithDetails[];
}

/** The store NAME a pending item belongs to, or null when none is assigned. */
export function storeOf(item: ShoppingListItemWithDetails): string | null {
  const name = item.display_store ?? item.store_name ?? null;
  return name && name.trim() !== '' ? name : null;
}

/** The store LOCATION id a pending item is assigned to, or null when none. */
export function storeLocationIdOf(
  item: ShoppingListItemWithDetails,
): string | null {
  const id = item.selected_storelocation_id ?? null;
  return id && id.trim() !== '' ? id : null;
}

/**
 * Group items by their assigned store LOCATION, preserving first-seen order so
 * the list is stable. Keys on `selected_storelocation_id` (T41) so two same-named
 * locations form two distinct groups; the visible label still reads the store
 * name. Unassigned items collect into one flat group rendered last (`storeId:
 * null`). Never fabricates a store for an item that has none.
 */
export function groupByStore(
  items: ShoppingListItemWithDetails[],
): StoreGroup[] {
  const groups = new Map<string, StoreGroup>();
  for (const item of items) {
    const name = storeOf(item);
    const storeId = storeLocationIdOf(item);
    // Key on the store LOCATION id when assigned, else the flat bucket. An item
    // with a name but no location id (legacy / unresolved pick) still groups
    // honestly under its name so the label isn't lost.
    const key = storeId ?? name ?? UNKNOWN_STORE;
    const label = name ?? UNKNOWN_STORE_LABEL;
    const existing = groups.get(key);
    if (existing) {
      existing.items.push(item);
    } else {
      groups.set(key, {
        key,
        label,
        storeId: storeId ?? null,
        items: [item],
      });
    }
  }
  // Render the unassigned group last so named stores lead.
  const ordered = [...groups.values()];
  ordered.sort((a, b) => {
    const aUnassigned = a.storeId == null && a.label === UNKNOWN_STORE_LABEL;
    const bUnassigned = b.storeId == null && b.label === UNKNOWN_STORE_LABEL;
    if (aUnassigned) return 1;
    if (bUnassigned) return -1;
    return 0;
  });
  return ordered;
}
